import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type RefObject,
  type Dispatch,
  type SetStateAction,
} from 'react'

import { type AgGridReact } from 'ag-grid-react'
import { type DataListPropsBase } from 'types/dataGridTypes'
import { clearMessageInfo } from 'types/types'
import { isEqualDate, isDateType, valueToDateStr } from 'utils/convertFromLocalDate'

import { useGridBase } from './useGridBase'

import type {
  DefaultMenuItem,
  CellValueChangedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  IMenuActionParams,
  MenuItemDef,
  ProcessDataFromClipboardParams,
  RowDataUpdatedEvent,
  Column,
  ValueFormatterParams,
} from 'ag-grid-community'

export type GridSelectionSummaryStats = {
  count: number
  sum: number
  min: number
  max: number
  avg: number
}

export function parseRenderedNumericValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value !== 'string') {
    return null
  }
  const normalized = value.replaceAll(',', '').trim()
  if (!normalized || normalized === '-' || normalized === '999 +') {
    return null
  }
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export function computeRenderAwareSelectionStats<TRowData = any>(
  api: GridApi<TRowData>
): GridSelectionSummaryStats | null {
  const ranges = (api.getCellRanges?.() ?? []) as Array<{
    startRow?: { rowIndex: number }
    endRow?: { rowIndex: number }
    columns?: Column[]
  }>
  if (ranges.length === 0) {
    return null
  }

  const visitedCells = new Set<string>()
  let count = 0
  let sum = 0
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  ranges.forEach((range) => {
    const start = Math.min(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0)
    const end = Math.max(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0)
    const columns = range.columns ?? []

    for (let rowIndex = start; rowIndex <= end; rowIndex += 1) {
      const rowNode = api.getDisplayedRowAtIndex(rowIndex)
      if (!rowNode) {
        // skip missing displayed row
      } else {
        for (let i = 0; i < columns.length; i += 1) {
          const column = columns[i]
          const colId = column.getColId()
          const cellKey = `${rowNode.id ?? rowIndex}:${colId}`
          if (!visitedCells.has(cellKey)) {
            visitedCells.add(cellKey)

            const rawValue = api.getCellValue({ rowNode, colKey: column })
            const colDef = column.getColDef()
            const { valueFormatter } = colDef
            const renderedValue = typeof valueFormatter === 'function'
              ? valueFormatter({
                value: rawValue,
                data: rowNode.data,
                node: rowNode,
                colDef,
                column,
                api,
                context: api.getGridOption('context'),
              } as ValueFormatterParams<TRowData, any>)
              : rawValue

            const numericValue = parseRenderedNumericValue(renderedValue)
            if (numericValue != null) {
              count += 1
              sum += numericValue
              min = Math.min(min, numericValue)
              max = Math.max(max, numericValue)
            }
          }
        }
      }
    }
  })

  if (count === 0) {
    return null
  }

  return {
    count,
    sum,
    min,
    max,
    avg: sum / count,
  }
}

interface GridEditProps extends DataListPropsBase {
  gridRef: RefObject<AgGridReact>
  isUpdating: boolean // 更新中（ローディング表示）
  columnMapping: Record<string, string> // importとrowDataValidateで使用するカラムマッピング
  primayKey: string[] // importとキー重複チェックに使うプライマリキー
  storageKey?: string
  updateData?: (updatedRows: any[]) => void // データ更新時のコールバック関数
  handleImportExcel?: (list: any[]) => { mappedData: any[]; hasError: boolean } // imort時のデータ変換コールバック関数
  createBlankRow: () => any // importと新規行追加に使う
  fieldValidate: (pRow: any, field: string | undefined, newValue: any) => boolean // 入力チェックのコールバック関数
  cellValueChanged?: (event: CellValueChangedEvent, haserror: boolean) => void // セル値変更時のコールバック関数
  setRowChangeTriggerRowID?: Dispatch<SetStateAction<string | null>> // 値変更時に、useStateの値を参照する処理を行う場合に使用
  scrollPositionKey?: string // スクロール位置維持に使用するキーフィールド名（例: 'orgItemCode', 'itemCode'）
  rowData?: any[] // グリッドの行データ（スクロール位置維持に必要）
}

/**
 * マスタメンテに代表されるシンプルなグリッド編集機能を提供するカスタムフック
 * 
 * @param param0 
 * @returns 
 */
export const useGridEdit = ({
  gridRef,
  isLoading,
  onUploadData,
  updateTrigger,
  setHasGridEdited,
  setResultMessage,
  isUpdating,
  columnMapping,
  primayKey,
  storageKey,
  updateData,
  handleImportExcel,
  createBlankRow,
  fieldValidate,
  cellValueChanged,
  setRowChangeTriggerRowID,
  scrollPositionKey,
  rowData,
}: GridEditProps) => {
  const [editing, setEditing] = useState(false)
  const [selectedScrollKey, setSelectedScrollKey] = useState<string | null>(null)

  // Maps PK
  const pkCacheRef = useRef<Map<string, string> | null>(null)
  const createBlankRowRef = useRef(createBlankRow)
  createBlankRowRef.current = createBlankRow

  // useGridBaseの機能を取得
  const gridCommon = useGridBase({
    gridRef,
    isLoading,
    isUpdating,
    storageKey,
  })

  // formattersオブジェクトから関数を取得
  const {
    isNonInputValue,
    parseNumeric,
    parsePositiveInteger,
    parseNumericWithRange,
    parseDate,
    parseDateValue,
    dateFormatter,
    dateTimeFormatter,
    parseTime,
    timeParser,
    parseStringValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    numberRangeValueParser,
    formatDecimalValue,
    parseDecimalValue,
    formatBooleanValue,
    parseBoolean,
    parseBooleanValue,
    booleanFormatter,
    selectValueFormatter,
    selectValueParser,
    typecodeFormatter,
    typeCodeParser,
    selectTypecodeValueParser,
    excelStyles,
    longTextValueParser,
    longTextValueFormatter,
    longTextEditorParams,
    longTextValueSetter,
    editInfoValueGetter,
    normalizeEmpty,
    isSameArrayUnordered,
  } = gridCommon

  useEffect(() => {
    // 更新ボタンが押下された場合
    if (updateTrigger === undefined || !updateData) return
    const updatedRows = beforeUpdate()

    if (updatedRows.length > 0) updateData(updatedRows)
  }, [updateTrigger])

  const clearPinnedRows = useCallback(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    api.setGridOption('pinnedTopRowData', [])
    api.setGridOption('pinnedBottomRowData', [])
    refreshPinnedRowsDisplay(api)
  }, [])

  /**
   * Generate a unique cache key from primary key field values
   * Uses valueToDateStr utility for consistent date formatting
   */
  const generatePKCacheKey = useCallback((row: any): string =>
    primayKey.map(field => {
      const value = row[field]
      if (value === null || value === undefined) return 'NULL'
      if (isDateType(value)) {
        return valueToDateStr(value) ?? 'NULL'
      }
      return String(value)
    }).join('|')
  , [primayKey])

  /**
   * Iterates through all grid nodes once and creates a Map of PK -> row ID
   * @returns Map<cacheKey, rowId>
   */
  const buildPKCache = useCallback((): Map<string, string> => {
    const cache = new Map<string, string>()
    if (!gridRef.current) return cache

    gridRef.current.api.forEachNode((node) => {
      if (node?.data?.id) {
        const key = generatePKCacheKey(node.data)
        cache.set(key, node.data.id)
      }
    })

    return cache
  }, [generatePKCacheKey])

  /**
   * Invalidate the primary key cache
   */
  const invalidatePKCache = useCallback(() => {
    pkCacheRef.current = null
  }, [])

  useEffect(() => {
    // インポートボタンが押下された場合、インポート処理を実行
    if (onUploadData && handleImportExcel) {
      const { mappedData, hasError } = handleImportExcel(onUploadData)
      afterImportExcel(mappedData, hasError)
      clearPinnedRows()
    }
  }, [onUploadData, clearPinnedRows])

  useEffect(() => {
    // 編集状態が変化した場合、親コンポーネントに通知
    setHasGridEdited(editing)
  }, [editing, setEditing])

  // スクロール位置維持: データ更新後に保存されたキーの行にスクロール
  useEffect(() => {
    if (!selectedScrollKey || !scrollPositionKey || !rowData) return
    const gridApi = gridRef.current?.api
    if (!gridApi) return

    gridApi.forEachNode((node) => {
      if (node.data && node.data[scrollPositionKey] === selectedScrollKey) {
        if (node.rowIndex !== null && node.rowIndex !== undefined) {
          gridApi.ensureIndexVisible(node.rowIndex, 'middle')
        }
      }
    })
  }, [rowData, selectedScrollKey, scrollPositionKey])

  /**
   * GridCommon
   */
  const onGridReady = ((event: GridReadyEvent<any, any>) => {
    // GridBaseのonGridReadyを呼び出し、その後編集機能特有の設定を追加
    const baseOnGridReady = gridCommon.onGridReady
    baseOnGridReady(event)
    
    // 編集機能特有の設定を追加
    event.api.updateGridOptions({
      onRowDataUpdated,
      onCellValueChanged,
      getContextMenuItems,
      processDataFromClipboard,
    })
  });

  const onRowDataUpdated = useCallback((event: RowDataUpdatedEvent) => {
    if (!event.api || event.api.isDestroyed()) return
    // setGridApi(event.api)

    // Invalidate PK cache when grid data is updated
    invalidatePKCache()

    if (editing) return
    let isedit = false
    event.api.forEachNode((node) => {
      if (node.data) {
        isedit = node.data.isEdited || isedit
      }
    })
    setEditing(isedit)
  }, [editing, invalidatePKCache])

  const processDataFromClipboard = (params: ProcessDataFromClipboardParams): string[][] | null => {
    // Info表示をリセット
    setResultMessage(clearMessageInfo())
    const { api } = params
    if (!api) return null

    const data = [...params.data]
    const lastIndex = api.getDisplayedRowCount() - 1
    const focusedCell = api.getFocusedCell()
    const focusedIndex = focusedCell!.rowIndex

    // 一番下に空白行がある場合、削除する
    const emptyLastRow = data[data.length - 1][0] === '' && data[data.length - 1].length === 1
    if (emptyLastRow) {
      data.splice(data.length - 1, 1)
    }

    const pasteRowSize = data.length

    // add new row
    if (focusedIndex + pasteRowSize - 1 > lastIndex) {
      const rowsToAdd: any[] = []
      const resultLastIndex = focusedIndex + (pasteRowSize - 1)
      const numRowsToAdd = resultLastIndex - lastIndex
      for (let i = 0; i < numRowsToAdd; i += 1) {
        rowsToAdd.push(createBlankRowRef.current())
      }
      api.applyTransaction({ add: rowsToAdd })
    }
    return data
  }

  const addRowAction = useCallback((actionParams: IMenuActionParams) => {
    // 右クリックの新規行追加選択時
    const { node, api } = actionParams
    if (!node) return
    const { data, rowIndex } = node

    if (api && data && rowIndex !== null) {
      const newRow = createBlankRowRef.current()
      newRow.isEdited = true

      const displayedRowIndex = node.rowIndex
      if (displayedRowIndex !== null) {
        const addIndex = displayedRowIndex + 1

        api.applyTransaction({
          add: [newRow],
          addIndex,
        })
        api.redrawRows()

        if (setRowChangeTriggerRowID) setRowChangeTriggerRowID(newRow.id)
      }
    }
  }, [setRowChangeTriggerRowID])

  const deleteRowAction = useCallback((actionParams: IMenuActionParams) => {
    // 右クリックの行削除選択時
    const { node, api } = actionParams
    if (!node) return

    const cellRanges = api.getCellRanges()
    if (!cellRanges || cellRanges.length === 0) return

    const { startRow, endRow } = cellRanges[0]
    const startIdx = startRow?.rowIndex
    const endIdx = endRow?.rowIndex
    if (startIdx === undefined || endIdx === undefined) return

    const from = Math.min(startIdx, endIdx)
    const to = Math.max(startIdx, endIdx)

    const rowsToRemove: any[] = []
    for (let i = from; i <= to; i += 1) {
      const rowNode = api.getDisplayedRowAtIndex(i)
      if (rowNode?.data && rowNode.data.isNewRecord) {
        rowsToRemove.push(rowNode.data)
      }
    }

    if (rowsToRemove.length > 0) {
      api.applyTransaction({ remove: rowsToRemove })
    }
  }, [])

  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
    const { topRowPinned, bottomRowPinned, dataRow } = isCrossAreaSelection(params)
    const { node } = params
    if (!node) {
      return [
        {
          name: 'コピーする',
          action: () => params.api.copySelectedRangeToClipboard(),
        },
        'separator',
        {
          name: '貼り付ける',
          action: () => params.api.pasteFromClipboard(),
          disabled: !gridCommon.isEnableEdit(),
        },
      ]
    }
    const { data, rowIndex } = node

    const result: (DefaultMenuItem | MenuItemDef)[] = [
      {
        name: '新規行を追加する',
        action: addRowAction,
        disabled: !gridCommon.isEnableEdit() || (topRowPinned || bottomRowPinned),
      },
      {
        name: '行を削除',
        action: deleteRowAction,
        disabled: !gridCommon.isEnableEdit(data ? data.isNewRecord : true) || (topRowPinned || bottomRowPinned),
      },
      'separator',
      {
        name: 'コピーする',
        action: () => params.api.copySelectedRangeToClipboard(),
        disabled: (topRowPinned || bottomRowPinned),
      },
      {
        name: '行コピーする',
        action: () => params.api.copySelectedRowsToClipboard(),
        disabled: (topRowPinned || bottomRowPinned),
      },
      'copyWithHeaders',
      'separator',
      {
        name: '貼り付ける',
        action: () => params.api.pasteFromClipboard(),
        disabled: !gridCommon.isEnableEdit() || (topRowPinned || bottomRowPinned),
      },
      'separator',
      {
        name: '行のピン留め',
        subMenu: [
          {
            name: '選択行を上にピン留め',
            action: () => {
              const selected = collectSelectedOrRangedRows(params)
              const currentTop = (params.api.getGridOption('pinnedTopRowData') as any[]) ?? []
              const merged = mergePinnedRows(currentTop, selected, 10)
              
              // Set the pinned top rows
              params.api.setGridOption('pinnedTopRowData', merged)
              refreshPinnedRowsDisplay(params.api)
            },
          },
          {
            name: '選択行を下にピン留め',
            action: () => {
              const selected = collectSelectedOrRangedRows(params)
              const currentBottom = (params.api.getGridOption('pinnedBottomRowData') as any[]) ?? []
              const merged = mergePinnedRows(currentBottom, selected, 10)
              // Set the pinned bottom rows
              params.api.setGridOption('pinnedBottomRowData', merged)
              refreshPinnedRowsDisplay(params.api)
            },
          },
          'separator',
          {
            name: '選択行上のピンを解除',
            action: () => {
              const currentTop = (params.api.getGridOption('pinnedTopRowData') as any[]) ?? []
              const selected = collectSelectedOrRangedRows(params, currentTop)
              // Remove selected rows from pinned top
              const selectedIds = new Set(selected.map(row => row.id))
              const filteredTop = currentTop.filter(row => !selectedIds.has(row.id))
              params.api.setGridOption('pinnedTopRowData', filteredTop)
              refreshPinnedRowsDisplay(params.api)
            },
            disabled: !topRowPinned,
          },
          {
            name: '選択行下のピンを解除',
            action: () => {
              const currentBottom = (params.api.getGridOption('pinnedBottomRowData') as any[]) ?? []
              const selected = collectSelectedOrRangedRows(params, currentBottom)

              // Remove selected rows from pinned bottom
              const selectedIds = new Set(selected.map(row => row.id))
              const filteredBottom = currentBottom.filter(row => !selectedIds.has(row.id))
              params.api.setGridOption('pinnedBottomRowData', filteredBottom)
              refreshPinnedRowsDisplay(params.api)
            },
            disabled: !bottomRowPinned,
          },
          'separator',
          {
            name: 'すべて上のピンを解除',
            action: () => params.api.setGridOption('pinnedTopRowData', []),
          },
          {
            name: 'すべて下のピンを解除',
            action: () => params.api.setGridOption('pinnedBottomRowData', []),
          },
          'separator',
          {
            name: 'すべてピンを解除',
            action: () => {
              params.api.setGridOption('pinnedTopRowData', [])
              params.api.setGridOption('pinnedBottomRowData', [])
              refreshPinnedRowsDisplay(params.api)
            },
          },
        ],
      }
    ]

    return result
  }, [addRowAction, deleteRowAction, gridCommon])

  // check if the range is cross area selection
  const isCrossAreaSelection = (params: GetContextMenuItemsParams) => {
    const ranges = params.api.getCellRanges() ?? []
    let topRowPinned = false
    let bottomRowPinned = false
    let dataRow = false
    if (ranges.length > 0) {
      ranges.forEach((r) => {
        const start = r.startRow?.rowPinned ?? null
        const end = r.endRow?.rowPinned ?? null
        if (start === "top" && end === "top") {
          topRowPinned = true
        }
        if (start === "bottom" && end === "bottom") {
          bottomRowPinned = true
        }
        if (start === null && end === null) {
          dataRow = true
        }
      })
    }
    return { topRowPinned, bottomRowPinned, dataRow }
  }

  // 選択（または範囲）から対象行データを収集
  const collectSelectedOrRangedRows = (params: GetContextMenuItemsParams, current: any[] = []): any[] => {
    // First check cell ranges (for multi-row selection by dragging)
    const ranges = params.api.getCellRanges() ?? []

    if (ranges.length > 0) {
      const rows: any[] = []
      ranges.forEach((r) => {
        const start = r.startRow?.rowIndex ?? 0
        const end = r.endRow?.rowIndex ?? start
        const from = Math.min(start, end)
        const to = Math.max(start, end)
        for (let i = from; i <= to; i += 1) {
          if (current.length > 0) {
            const rowNode = current[i]
            if (rowNode) rows.push(rowNode)
          } else {
          const rowNode = params.api.getDisplayedRowAtIndex(i)
            if (rowNode?.data) rows.push(rowNode.data)
          }
        }
      })
      
      // Remove duplicates
      const seen = new Set<string>()
      const uniq: any[] = []
      rows.forEach((r) => {
        const key = r?.id ?? JSON.stringify(r)
        if (!seen.has(key)) {
          seen.add(key)
          uniq.push(r)
        }
      })
      return uniq
    }
    
    // Fallback to selected rows
    const selected = params.api.getSelectedRows() ?? []
    return selected
  }

  // 既存ピン留めとマージし、最大数を制限
  const mergePinnedRows = (current: any[], add: any[], limit: number): any[] => {
    const map = new Map<string, any>()
    const push = (arr: any[]) => {
      arr.forEach((r) => {
        const key = r?.id ?? JSON.stringify(r)
        if (!map.has(key)) map.set(key, r)
      })
    }
    push(current)
    push(add)
    const result = Array.from(map.values()).slice(0, limit)
    return result
  }

  // ピン留め行の表示を更新する共通関数
  const refreshPinnedRowsDisplay = useCallback((api: any) => {
    setTimeout(() => {
      api.refreshCells()
      api.redrawRows()
    }, 10)
  }, [])

  const checkAndSetError = useCallback(
    (pRow: any, field: string, error: string | null): boolean => {
      if (!field) return false
      if (!gridRef.current) return false

      let hasErrors = false
      // const trRow = pRow
      const rowNode = gridRef.current.api.getRowNode(pRow.id || '')
      const trRow = pRow ?? rowNode?.data
      // console.log("fook!checkAndSetError",trRow, rowNode)
      if (error) {
        trRow.errors[field] = [error]
        trRow.editedFields.add(field)
        //   if (!trRow.editedFields.includes(field)) trRow.editedFields.push(field)
        hasErrors = true
      } else if (trRow.errors && trRow.errors[field]) {
        delete trRow.errors[field]
      }
      rowNode?.updateData(trRow)
      return hasErrors
    },
    [gridRef.current]
  ) // gridApi を依存配列に追加

  /**
   * Check for duplicate primary keys
   */
  const duplicateCheck = useCallback((pRow: any, newValue: any): boolean => {
    if (!gridRef.current) return false

    // Build cache if not exists
    if (!pkCacheRef.current) {
      pkCacheRef.current = buildPKCache()
    }

    // Generate cache key for current row
    const currentKey = generatePKCacheKey(pRow)

    const existingRowId = pkCacheRef.current.get(currentKey)
    const hasDuplicate = existingRowId && existingRowId !== pRow.id

    if (hasDuplicate) {
      primayKey.forEach((field) => {
        checkAndSetError(pRow, field, '入力値はすでに登録されています')
      })
      return true
    }

    return false
  }, [primayKey, checkAndSetError, buildPKCache, generatePKCacheKey])
  
  const colDataValidate = useCallback((pRow: any, field: string | undefined, newValue: any): boolean => {
    // console.log("hooks!colDataValidate", field, gridApi)
    if (!field) return false
    if (!gridRef.current) return false

    let hasErrors = false
    const rowNode = gridRef.current.api.getRowNode(pRow.id || '')
    const trRow = pRow ?? rowNode?.data

    checkAndSetError(trRow, field, '')
    hasErrors = fieldValidate(trRow, field, newValue)
    // console.log("colDataValidate", field, newValue, trRow, hasErrors)
    return hasErrors
  }, [checkAndSetError, fieldValidate])

  /**
   * 行に含まれる項目に入力チェックを実施
   * validate all cell in row
   * @param pRow
   * @returns
   */
  const rowDataValidate = (pRow: any): boolean => {
    if (!pRow) return false
    const trRow = pRow
    let hasErrors = false
    trRow.errors = {}
    Object.keys(columnMapping).forEach((key) => {
      const field = columnMapping[key]
      const fieldValue = trRow[field as keyof typeof trRow]
      // onCellValueChanged と同様に undefined を null に正規化してから検証する
      // （API が null フィールドを省略すると undefined になり、登録時のみ誤検知するのを防ぐ）
      colDataValidate(trRow, field, normalizeEmpty(fieldValue))
    })

    if (trRow.errors && Object.keys(trRow.errors).length > 0) {
      hasErrors = true
    }
    return hasErrors
  }

  /**
   * セルの変更イベントの共通処理
   * 個別処理が必要な場合は、cellValueChangedを定義すると、共通処理の後に個別処理が実施される
   *
   */
  /**
   * ピン留めされた行と通常の行の間でデータを同期する
   * Synchronize data between pinned rows and regular rows
   */
  const syncPinnedRows = useCallback((updatedRow: any, field: string, newValue: any) => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return

    // 更新された行のIDを取得
    const rowId = updatedRow.id
    if (!rowId) return

    // ピン留めされた上部の行を取得
    const pinnedTopRows = api.getGridOption('pinnedTopRowData') as any[] || []
    // ピン留めされた下部の行を取得
    const pinnedBottomRows = api.getGridOption('pinnedBottomRowData') as any[] || []

    let hasChanges = false

    // 上部のピン留め行を更新
    const updatedTopRows = pinnedTopRows.map((pinnedRow: any) => {
      if (pinnedRow.id === rowId) {
        hasChanges = true
        return { ...pinnedRow, [field]: newValue }
      }
      return pinnedRow
    })

    // 下部のピン留め行を更新
    const updatedBottomRows = pinnedBottomRows.map((pinnedRow: any) => {
      if (pinnedRow.id === rowId) {
        hasChanges = true
        return { ...pinnedRow, [field]: newValue }
      }
      return pinnedRow
    })

    // 変更があった場合、ピン留め行を更新
    if (hasChanges) {
      if (updatedTopRows.length > 0) {
        api.setGridOption('pinnedTopRowData', updatedTopRows)
      }
      if (updatedBottomRows.length > 0) {
        api.setGridOption('pinnedBottomRowData', updatedBottomRows)
      }
      refreshPinnedRowsDisplay(api)
    }
  }, [refreshPinnedRowsDisplay])

  /**
   * ピン留め行から通常の行と他のピン留め行にデータを同期する
   * Synchronize data from pinned rows to regular rows and other pinned rows
   */
  const syncRegularRows = useCallback((updatedRow: any, field: string, newValue: any) => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return

    // 更新された行のIDを取得
    const rowId = updatedRow.id
    if (!rowId) return

    // 通常の行データを取得して更新
    api.forEachNode((node) => {
      if (!node.rowPinned && node.data && node.data.id === rowId) {
        const regularRowData = { ...node.data, [field]: newValue }
        node.setData(regularRowData)
      }
    })

    // 他のピン留め行も更新（top/bottom pin間の同期）
    const pinnedTopRows = api.getGridOption('pinnedTopRowData') as any[] || []
    const pinnedBottomRows = api.getGridOption('pinnedBottomRowData') as any[] || []

    let hasChanges = false

    // 上部のピン留め行を更新（bottom pinから変更された場合）
    const updatedTopRows = pinnedTopRows.map((pinnedRow: any) => {
      if (pinnedRow.id === rowId) {
        hasChanges = true
        return { ...pinnedRow, [field]: newValue }
      }
      return pinnedRow
    })

    // 下部のピン留め行を更新（top pinから変更された場合）
    const updatedBottomRows = pinnedBottomRows.map((pinnedRow: any) => {
      if (pinnedRow.id === rowId) {
        hasChanges = true
        return { ...pinnedRow, [field]: newValue }
      }
      return pinnedRow
    })

    // 変更があった場合、ピン留め行を更新
    if (hasChanges) {
      if (updatedTopRows.length > 0) {
        api.setGridOption('pinnedTopRowData', updatedTopRows)
      }
      if (updatedBottomRows.length > 0) {
        api.setGridOption('pinnedBottomRowData', updatedBottomRows)
      }
      refreshPinnedRowsDisplay(api)
    }
  }, [refreshPinnedRowsDisplay])

  /**
   * すべての行（ピン留め行と通常の行）の間でデータを同期する
   * Synchronize data between all rows (pinned and regular)
   */
  const syncAllRows = useCallback((updatedRow: any, field: string, newValue: any) => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return

    const rowId = updatedRow.id
    if (!rowId) return

    // 通常の行を更新
    api.forEachNode((node) => {
      if (!node.rowPinned && node.data && node.data.id === rowId) {
        const regularRowData = { ...node.data, [field]: newValue }
        node.setData(regularRowData)
      }
    })

    // ピン留め行を更新
    const pinnedTopRows = api.getGridOption('pinnedTopRowData') as any[] || []
    const pinnedBottomRows = api.getGridOption('pinnedBottomRowData') as any[] || []

    let hasChanges = false

    // 上部のピン留め行を更新
    const updatedTopRows = pinnedTopRows.map((pinnedRow: any) => {
      if (pinnedRow.id === rowId) {
        hasChanges = true
        return { ...pinnedRow, [field]: newValue }
      }
      return pinnedRow
    })

    // 下部のピン留め行を更新
    const updatedBottomRows = pinnedBottomRows.map((pinnedRow: any) => {
      if (pinnedRow.id === rowId) {
        hasChanges = true
        return { ...pinnedRow, [field]: newValue }
      }
      return pinnedRow
    })

    // 変更があった場合、ピン留め行を更新
    if (hasChanges) {
      if (updatedTopRows.length > 0) {
        api.setGridOption('pinnedTopRowData', updatedTopRows)
      }
      if (updatedBottomRows.length > 0) {
        api.setGridOption('pinnedBottomRowData', updatedBottomRows)
      }
      refreshPinnedRowsDisplay(api)
    }
  }, [refreshPinnedRowsDisplay])

  const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    // Info表示をリセット
    setResultMessage(clearMessageInfo())
    const { data, colDef, newValue, oldValue, node, api } = event
    const { field } = colDef
    if (!field || !api || api.isDestroyed()) return
    // setGridApi(event.api)
    // console.log("onCellValueChanged change", field, newValue, oldValue)

    const updatedRow = data
    // 値の変更時に、APIのエラーを解消
    if (primayKey.indexOf(field) >= 0) {
      primayKey.forEach((key) => {
        // haserror = checkAndSetError(updatedRow, key, updatedRow[key]) || haserror
        checkAndSetError(updatedRow, key, '')
      })
    }
    const haserror = colDataValidate(updatedRow, field, normalizeEmpty(newValue))

    let isSameArrayValue = false
    let newValueChange = newValue
    // AG-Grid bug: CheckboxCellRenderer.onCheckboxChanged (main.esm.mjs:17019) sets
    // newValue = params.value (stale old value) instead of isSelected (actual toggled value).
    // data[field] always has the correct current value after checkbox toggle.
    if (colDef?.cellEditor === 'agCheckboxCellEditor') {
       newValueChange = updatedRow[field]
    }
    let isSameValue = isDateType(updatedRow[field]) ? isEqualDate(newValueChange, oldValue) : normalizeEmpty(newValueChange) === normalizeEmpty(oldValue)
    if (Array.isArray(newValueChange) && Array.isArray(oldValue)) {
      isSameArrayValue = isSameArrayUnordered(newValueChange, oldValue)
    }
    if (isSameArrayValue) {
      isSameValue = isSameArrayValue
    }
    if (!isSameValue) {
      setEditing(true)
      updatedRow.isEdited = true
      updatedRow.editedFields.add(field as string)
      // console.log("onCellValueChanged change", field, newValue, oldValue, updatedRow, haserror)

      if (primayKey.indexOf(field) >= 0) {
        invalidatePKCache()
      }
    }

    // set date if user edit manual
    if (isDateType(updatedRow[field])) {
      const parsedDate = parseDate(normalizeEmpty(newValue))
      if (parsedDate === null) {
        updatedRow[field] = null
      } else {
        updatedRow[field] = parsedDate
      }
    }

    node.setData(updatedRow)

    // ピン留め行と通常の行の間でデータを同期
    if (!node.rowPinned) {
      // 通常の行で変更があった場合、対応するピン留め行も更新
      syncPinnedRows(updatedRow, field, updatedRow[field])
    } else {
      // ピン留め行で変更があった場合、対応する通常の行と他のピン留め行も更新
      syncRegularRows(updatedRow, field, updatedRow[field])
    }

    // DataList側でセル変更時の個別処理を実施
    // カスタムフックから直接コンポーネントの状態にアクセスすることはできない
    // useStateの値を参照する処理がある場合、setRowChangeTriggerRowIDを設定してEffectで処理する必要がある
    if (cellValueChanged) cellValueChanged(event, haserror)
    if (setRowChangeTriggerRowID) setRowChangeTriggerRowID(updatedRow.id)
  }, [setResultMessage, primayKey, checkAndSetError, colDataValidate, setEditing, parseDate, cellValueChanged, setRowChangeTriggerRowID, syncPinnedRows, syncRegularRows, invalidatePKCache])

  /**
   * データ更新の前処理
   * 入力チェックを実施し、変更対象行のみ返す
   */
  const beforeUpdate = useCallback((): any[] => {
    setResultMessage(clearMessageInfo())
    if (!gridRef.current?.api || !gridRef.current.api) return []
    
    // スクロール位置維持: 更新前に現在フォーカスされている行のキーを保存
    if (scrollPositionKey) {
      const { api } = gridRef.current
      const focused = api.getFocusedCell()
      if (focused) {
        const node = api.getDisplayedRowAtIndex(focused.rowIndex)
        const focusedRowData = node?.data
        if (focusedRowData && focusedRowData[scrollPositionKey]) {
          setSelectedScrollKey(focusedRowData[scrollPositionKey])
        }
      }
    }
    
    let hasErrors = false

    const updatedRows: any[] = []
    
    gridRef.current.api.forEachNode((node) => {

      if (node.data) {
        // console.log("beforeUpdate", node.data.isEdited, node.data.editedFields.size, node.data)
        // 変更がない行（追加しただけの新規行）はskip
        if (node.data.editedFields?.size > 0) {

          hasErrors = rowDataValidate(node.data) || hasErrors
          if (node.data.isEdited) updatedRows.push(node.data)
        }
      }
    })
    // console.log("fook!beforeUpdate",updatedRows, hasErrors)

    if (hasErrors) {
      // console.log("hasErrors", updatedRows)
      setResultMessage({
        status: 'error',
        message: '入力エラーがあります',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      gridRef.current.api.applyTransaction({ update: updatedRows })
      gridRef.current.api.redrawRows()
      return []
    }

    if (updatedRows.length === 0) {
      setResultMessage({
        status: 'warning',
        message: '更新対象のデータがありません',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    }

    return updatedRows
  }, [setResultMessage, rowDataValidate, scrollPositionKey])

  /**
   * ファイルインポートの共通後処理
   * 各機能のコンポーネントで型変換などを済ませた後、グリッドに取り込む
   * その際、新規と更新の判定や、変更箇所のマークをつける
   */
  const afterImportExcel = useCallback((importList: any[], hasError: boolean) => {
    // console.log("handleImportExcel", hasError, importList)
    if (importList.length === 0) return
    if (hasError) {
      setResultMessage({
        status: 'warning',
        message: '取り込めない値はスキップされました',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    } else {
      // Info表示をリセット
      setResultMessage(clearMessageInfo())
    }

    if (!gridRef.current?.api) return
    const gridRows = gridCommon.getRowData()

    // console.log("fook!afterImportExcel",importList,gridRows)
    const newRows: any[] = []
    const editedRowsTemp: any[] = []

    importList.forEach((importRow) => {
      // console.log("for importList",importRow)
      const updateGridRow = gridRows.find((row) =>
        primayKey.every((field) =>
          isDateType(row[field]) ? isEqualDate(importRow[field], row[field]) : importRow[field] === row[field]
        )
      )
      // console.log("find",updateGridRow)
      if (updateGridRow) {
        // 既存データの更新
        let editCount = 0
        Object.keys(importRow).forEach((field) => {
          const importedValue = importRow[field] ?? null
          const existingValue = updateGridRow[field] ?? null

          if (!Object.values(columnMapping).includes(field)) {
            // skip
          } else if (isDateType(existingValue)) {
            if (!isEqualDate(importedValue, existingValue)) {
              updateGridRow.editedFields.add(field)
              editCount += 1
            }
          } else if (String(normalizeEmpty(importedValue)) !== String(normalizeEmpty(existingValue))) {
            updateGridRow.editedFields.add(field)
            editCount += 1
          }
        })
        if (editCount > 0) {
          const updatedRow = {
            ...updateGridRow,
            ...importRow,
            isEdited: true,
            errors: {},
          }
          // 入力チェック
          rowDataValidate(updatedRow)
          // console.log("fook!afterImportExcel 既存データ",updatedRow)
          editedRowsTemp.push(updatedRow)
        }
      } else {
        // 新規データの追加
        const newRow = {
          ...createBlankRowRef.current(),
          ...importRow,
          isEdited: true,
          errors: {},
          // editedFields:Object.keys(importRow)
          editedFields: new Set(Object.values(columnMapping)),
        }
        // console.log("fook!afterImportExcel 新規データの追加",newRow)
        rowDataValidate(newRow)
        newRows.push(newRow)
      }
    })
    // console.log("fook!afterImportExcel after",newRows, editedRowsTemp)
    gridRef.current.api.applyTransaction({ add: newRows, update: editedRowsTemp });
    gridRef.current.api.refreshCells();

    invalidatePKCache()
  }, [setResultMessage, gridCommon, primayKey, columnMapping, rowDataValidate, invalidatePKCache])


  return {
    // GridBaseからの機能を展開（formatters機能も含む）
    ...gridCommon,
    // useGridEdit固有のプロパティ
    onGridReady, // 上書きして編集機能を追加
    getContextMenuItems,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    // ピン留め行同期機能
    syncPinnedRows,
    syncRegularRows,
    syncAllRows,
    clearPinnedRows,
  }
}

/**
 * アラートダイアログ等: セル範囲・行選択・右クリック時のノードを統合して IRowNode を返す
 * （内部の collectSelectedOrRangedRows は行 data を返すため、ノードが必要な箇所用）
 */
export function collectRowNodesFromGridSelection(
  api: GridApi,
  fallbackNode: IRowNode | null | undefined
): IRowNode[] {
  const ranges = api.getCellRanges?.() ?? []
  if (ranges.length > 0) {
    const nodes: IRowNode[] = []
    const seen = new Set<string>()
    ranges.forEach((r) => {
      const start = r.startRow?.rowIndex ?? 0
      const end = r.endRow?.rowIndex ?? start
      const from = Math.min(start, end)
      const to = Math.max(start, end)
      for (let i = from; i <= to; i += 1) {
        const rowNode = api.getDisplayedRowAtIndex(i)
        const id = rowNode?.data?.id as string | undefined
        if (rowNode && id && !seen.has(id)) {
          seen.add(id)
          nodes.push(rowNode)
        }
      }
    })
    if (nodes.length > 0) return nodes
  }

  const selected = api.getSelectedNodes?.() ?? []
  if (selected.length > 0) return selected

  if (fallbackNode) return [fallbackNode]
  return []
}

/** getContextMenuItems 内のコピー系と同じ（locale の copyWithHeaders と併用） */
export function getClipboardContextMenuItems(
  params: GetContextMenuItemsParams
): (DefaultMenuItem | MenuItemDef)[] {
  return [
    {
      name: 'コピーする',
      action: () => params.api.copySelectedRangeToClipboard(),
    },
    {
      name: '行コピーする',
      action: () => params.api.copySelectedRowsToClipboard(),
    },
    'copyWithHeaders',
  ]
}
